import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  UserPlus,
  Loader2,
  Trash2,
  Copy,
  ShieldOff,
  ShieldCheck,
  Users as UsersIcon,
  MapPin,
  ChevronDown,
  CreditCard,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import PasswordInput from "@/components/PasswordInput";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import UserBillingDrawer from "@/components/billing/admin/UserBillingDrawer";

const ROLE_LABEL = { admin: "Admin", member: "Member" };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [billingUser, setBillingUser] = useState(null);
  const [issued, setIssued] = useState(null); // { user, temp_password }
  const [confirmState, setConfirmState] = useState(null); // { title, description, confirmLabel, destructive, onConfirm }

  const load = async () => {
    try {
      const [usersRes, locsRes] = await Promise.all([
        api.get("/users"),
        api.get("/locations"),
      ]);
      setUsers(usersRes.data);
      setLocations(locsRes.data?.locations || []);
    } catch (e) {
      toast.error("Failed to load users", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toggleDisabled = (u) => {
    const target = !u.disabled;
    setConfirmState({
      title: target ? `Disable ${u.email}?` : `Re-enable ${u.email}?`,
      description: target
        ? "Disabled users can't log in. Their data stays intact."
        : "User will be able to log in again.",
      confirmLabel: target ? "Disable" : "Re-enable",
      destructive: target,
      onConfirm: async () => {
        try {
          await api.patch(`/users/${u.id}`, { disabled: target });
          toast.success(target ? "User disabled" : "User re-enabled");
          load();
        } catch (e) {
          toast.error("Update failed", { description: formatApiError(e) });
        }
      },
    });
  };

  const remove = (u) => {
    setConfirmState({
      title: `Delete ${u.email}?`,
      description: "This permanently removes the user. Data in their assigned locations stays intact.",
      confirmLabel: "Delete user",
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/users/${u.id}`);
          toast.success("User deleted");
          load();
        } catch (e) {
          toast.error("Delete failed", { description: formatApiError(e) });
        }
      },
    });
  };

  const resetPassword = (u) => {
    setConfirmState({
      title: `Reset password for ${u.email}?`,
      description:
        "The user's current password will be replaced with Password1234!. Share it with them securely — they can change it from Profile → Settings after logging in.",
      confirmLabel: "Reset password",
      destructive: true,
      onConfirm: async () => {
        try {
          const { data } = await api.post(`/users/${u.id}/reset-password`);
          toast.success("Password reset", {
            description: `${u.email} can now log in with the shared default.`,
          });
          setIssued({ user: data.user, temp_password: data.temp_password });
        } catch (e) {
          toast.error("Reset failed", { description: formatApiError(e) });
        }
      },
    });
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`Role set to ${ROLE_LABEL[role]}`);
      load();
    } catch (e) {
      toast.error("Role change failed", { description: formatApiError(e) });
    }
  };

  const changeLocations = async (u, locationIds) => {
    try {
      await api.patch(`/users/${u.id}`, { location_ids: locationIds });
      toast.success("Assigned locations updated");
      load();
    } catch (e) {
      toast.error("Location update failed", { description: formatApiError(e) });
    }
  };

  const changeTerritoryAccess = async (u, val) => {
    // Optimistic — flip the row locally so the switch feels instant.
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, territory_access: val } : x)));
    try {
      await api.patch(`/users/${u.id}`, { territory_access: val });
      toast.success(val ? "Territory access granted" : "Territory access revoked");
    } catch (e) {
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, territory_access: !val } : x)));
      toast.error("Update failed", { description: formatApiError(e) });
    }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Admin
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-display">
            Users
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Invite teammates, change roles, disable accounts. Assign each user to
            one or more locations — they'll only see data for those locations.
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          className="text-white min-h-[44px]"
          style={{ background: "hsl(var(--brand))" }}
          data-testid="invite-user-button"
        >
          <UserPlus className="h-4 w-4 mr-2" /> Invite user
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 p-6">Loading…</div>
      ) : users.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
          <UsersIcon className="h-6 w-6 mx-auto text-slate-300 mb-2" />
          No users yet.
        </div>
      ) : (
        <>
          {/* desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm data-table">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Locations</th>
                  <th className="px-3 py-2 text-left">Territory</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Last login</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isMe = u.id === me?.id;
                  const uIsAdmin = u.role === "admin";
                  return (
                    <tr
                      key={u.id}
                      className="border-t border-slate-200"
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="px-3 py-2 align-middle text-slate-900">
                        {u.email}
                        {isMe ? (
                          <span className="ml-2 text-xs text-slate-500">(you)</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-middle text-slate-700">
                        {u.name || "—"}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <Select
                          value={u.role}
                          onValueChange={(v) => changeRole(u, v)}
                          disabled={isMe}
                        >
                          <SelectTrigger
                            className="h-8 w-28"
                            data-testid={`user-role-${u.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 align-middle text-slate-600">
                        <LocationAssignPopover
                          user={u}
                          locations={locations}
                          onSave={(ids) => changeLocations(u, ids)}
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={uIsAdmin ? true : !!u.territory_access}
                            disabled={uIsAdmin}
                            onCheckedChange={(v) => changeTerritoryAccess(u, v)}
                            data-testid={`user-territory-${u.id}`}
                          />
                          {uIsAdmin ? (
                            <span className="text-[10px] text-slate-400">always on</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-middle">
                        {u.disabled ? (
                          <span className="px-2 py-0.5 rounded border bg-red-50 text-red-700 border-red-200 text-xs">
                            disabled
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                            active
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-middle text-slate-500 text-xs">
                        {u.last_login ? fmtTime(u.last_login) : "—"}
                      </td>
                      <td className="px-3 py-2 align-middle text-right">
                        <div className="flex justify-end gap-1">
                          {!uIsAdmin ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setBillingUser(u)}
                              title="Billing & credits"
                              data-testid={`billing-user-${u.id}`}
                            >
                              <CreditCard className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                          {!isMe ? (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => resetPassword(u)}
                                title="Reset password to Password1234!"
                                data-testid={`reset-password-${u.id}`}
                              >
                                <KeyRound className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleDisabled(u)}
                                title={u.disabled ? "Re-enable" : "Disable"}
                                data-testid={`toggle-user-${u.id}`}
                              >
                                {u.disabled ? (
                                  <ShieldCheck className="h-3.5 w-3.5" />
                                ) : (
                                  <ShieldOff className="h-3.5 w-3.5" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => remove(u)}
                                title="Delete"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 pr-2">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* mobile cards */}
          <ul className="md:hidden space-y-3">
            {users.map((u) => {
              const isMe = u.id === me?.id;
              const uIsAdmin = u.role === "admin";
              return (
                <li
                  key={u.id}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                  data-testid={`user-card-${u.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {u.email}{" "}
                        {isMe ? (
                          <span className="text-xs text-slate-500">(you)</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {u.name || "—"} ·{" "}
                        {(u.location_ids || [])
                          .map((id) => locations.find((l) => l.id === id)?.name)
                          .filter(Boolean)
                          .join(", ") || "No locations"}
                      </div>
                      <div className="text-xs mt-1">
                        Role:{" "}
                        <span className="font-medium capitalize text-slate-700">
                          {u.role}
                        </span>{" "}
                        ·{" "}
                        {u.disabled ? (
                          <span className="text-red-700">disabled</span>
                        ) : (
                          <span className="text-emerald-700">active</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs mt-2">
                        <span className="text-slate-500">Territory access</span>
                        <Switch
                          checked={uIsAdmin ? true : !!u.territory_access}
                          disabled={uIsAdmin}
                          onCheckedChange={(v) => changeTerritoryAccess(u, v)}
                          data-testid={`user-territory-mobile-${u.id}`}
                        />
                        {uIsAdmin ? (
                          <span className="text-[10px] text-slate-400">always on</span>
                        ) : null}
                      </div>
                    </div>
                    {!isMe ? (
                      <div className="flex gap-1 shrink-0">
                        {!uIsAdmin ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setBillingUser(u)}
                            className="min-h-[44px]"
                            title="Billing & credits"
                            data-testid={`billing-user-mobile-${u.id}`}
                          >
                            <CreditCard className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resetPassword(u)}
                          className="min-h-[44px]"
                          data-testid={`reset-password-mobile-${u.id}`}
                        >
                          <KeyRound className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleDisabled(u)}
                          className="min-h-[44px]"
                        >
                          {u.disabled ? "Enable" : "Disable"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => remove(u)}
                          className="min-h-[44px]"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <InviteUserDialog
        open={inviteOpen}
        locations={locations}
        onClose={() => setInviteOpen(false)}
        onIssued={(data) => {
          setInviteOpen(false);
          setIssued(data);
          load();
        }}
      />

      <TempPasswordDialog
        data={issued}
        onClose={() => setIssued(null)}
      />

      <UserBillingDrawer
        user={billingUser}
        open={!!billingUser}
        onClose={() => setBillingUser(null)}
      />

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel}
        destructive={confirmState?.destructive}
        onConfirm={confirmState?.onConfirm}
        onClose={() => setConfirmState(null)}
      />
    </div>
  );
}

function LocationAssignPopover({ user, locations, onSave }) {
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState(new Set(user.location_ids || []));

  useEffect(() => {
    setIds(new Set(user.location_ids || []));
  }, [user.location_ids]);

  const names = (user.location_ids || [])
    .map((id) => locations.find((l) => l.id === id)?.name)
    .filter(Boolean);

  const toggle = (id) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const apply = () => {
    setOpen(false);
    onSave(Array.from(ids));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 max-w-[190px] justify-between font-normal"
          data-testid={`user-locations-${user.id}`}
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate text-xs">
              {names.length === 0
                ? "None"
                : names.length === 1
                ? names[0]
                : `${names.length} locations`}
            </span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3" align="start">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
          Assigned locations
        </div>
        <div className="max-h-52 overflow-y-auto space-y-0.5">
          {locations.map((loc) => (
            <label
              key={loc.id}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-100"
            >
              <Checkbox
                checked={ids.has(loc.id)}
                onCheckedChange={() => toggle(loc.id)}
                data-testid={`assign-location-${user.id}-${loc.id}`}
              />
              <span className="truncate text-slate-800">{loc.name}</span>
            </label>
          ))}
          {locations.length === 0 ? (
            <div className="text-xs text-slate-500 px-2 py-1.5">
              No locations yet — create one on the Locations page.
            </div>
          ) : null}
        </div>
        <Button
          size="sm"
          onClick={apply}
          className="w-full mt-2 text-white min-h-[36px]"
          style={{ background: "hsl(var(--brand))" }}
          data-testid={`assign-location-save-${user.id}`}
        >
          Save
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function InviteUserDialog({ open, locations, onClose, onIssued }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("member");
  const [location, setLocation] = useState("");
  const [locationIds, setLocationIds] = useState(new Set());
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setName("");
      setRole("member");
      setLocation("");
      setLocationIds(new Set());
      setPassword("");
      setBusy(false);
    }
  }, [open]);

  const toggleLoc = (id) => {
    setLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { email, name, role, location_ids: Array.from(locationIds) };
      if (location.trim()) body.location = location.trim();
      if (password.trim()) body.password = password.trim();
      const { data } = await api.post("/users", body);
      toast.success("Invited", { description: "Share the temp password securely." });
      onIssued(data);
    } catch (err) {
      toast.error("Invite failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invite-user-dialog">
        <DialogHeader>
          <DialogTitle>Invite a user</DialogTitle>
          <DialogDescription>
            Leave the password blank to auto-generate a temporary one.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Email
            </Label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 min-h-[44px]"
              data-testid="invite-email-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                Name
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 min-h-[44px]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                Role
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1 min-h-[44px]" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Assigned locations (what data they can see)
            </Label>
            <div className="mt-1 rounded-md border border-slate-200 p-2 space-y-0.5 max-h-40 overflow-y-auto">
              {locations.map((loc) => (
                <label
                  key={loc.id}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-100"
                >
                  <Checkbox
                    checked={locationIds.has(loc.id)}
                    onCheckedChange={() => toggleLoc(loc.id)}
                    data-testid={`invite-location-checkbox-${loc.id}`}
                  />
                  <span className="truncate text-slate-800">{loc.name}</span>
                </label>
              ))}
              {locations.length === 0 ? (
                <div className="text-xs text-slate-500 px-2 py-1.5">
                  No locations yet — create one on the Locations page first.
                </div>
              ) : null}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Location (used for their scraping queries)
            </Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Saskatoon, SK"
              className="mt-1 min-h-[44px]"
              data-testid="invite-location-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Initial password (optional)
            </Label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave blank to auto-generate"
              className="mt-1 min-h-[44px]"
              data-testid="invite-password-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="min-h-[44px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="text-white min-h-[44px]"
              style={{ background: "hsl(var(--brand))" }}
              data-testid="invite-submit-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TempPasswordDialog({ data, onClose }) {
  const copy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Copied"))
      .catch(() => {
        // iframe-safe fallback
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        try {
          document.execCommand("copy");
          toast.success("Copied");
        } catch (_) {
          toast.error("Copy failed — long-press to copy manually");
        }
        document.body.removeChild(el);
      });
  };
  if (!data) return null;
  const isReset = data.user?.id && !data.user?.role; // reset endpoint returns just id+email
  const title = isReset ? "Password reset" : "Temporary password";
  const description = isReset
    ? `Share this new password with ${data.user.email} securely. They can change it later from Profile → Settings.`
    : `This is shown ONCE. Share it with ${data.user.email} securely.`;
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="text-xs text-amber-800 mb-1">
            For {data.user.email}
          </div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm bg-white border border-amber-200 rounded px-2 py-1 break-all flex-1">
              {data.temp_password}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copy(data.temp_password)}
              data-testid="temp-password-copy"
              className="min-h-[44px]"
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="min-h-[44px]">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || "";
  }
}
